import React, { useCallback, useEffect, useState } from "react";
import { Users2, Crown, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { JoinTeamsPanel } from "../components/teams/JoinTeamsPanel";
import { TeamManagePanel } from "../components/teams/TeamManagePanel";
import { TeamNotices } from "../components/teams/TeamNotices";
import { TeamRota } from "../components/teams/TeamRota";
import { MyTeamInvites } from "../components/teams/TeamInvitePanel";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const TeamCard = ({ t, reload }) => (
  <div className={`${CARD} p-6`} data-testid={`my-team-${t.id}`}>
    <div className="flex flex-wrap items-start gap-3">
      <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#17A88A1A" }}><Users2 className="h-6 w-6" style={{ color: "#17A88A" }} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-head text-xl font-bold">{t.name}</h2>
          <span data-testid={`my-team-role-${t.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={t.can_manage ? { background: "#FEF6D8", color: "#8a6a00" } : { background: "#0B5E4E14", color: "var(--sj-primary)" }}>{t.my_role}</span>
          {t.can_manage && t.applications.length > 0 && <span data-testid={`my-team-pending-${t.id}`} className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: "#C0392B" }}>{t.applications.length} waiting</span>}
        </div>
        {t.description && <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>{t.description}</p>}
        <div className="text-sm mt-1 flex items-center gap-1" style={{ color: "var(--sj-text-2)" }}><Crown className="h-3.5 w-3.5" style={{ color: "var(--sj-ochre)" }} /> {t.leaders.join(", ") || "No leader assigned yet"} · {t.members.length} members</div>
      </div>
    </div>
    <TeamNotices team={t} reload={reload} />
    <TeamRota team={t} reload={reload} />
    {t.can_manage ? <TeamManagePanel team={t} reload={reload} /> : (
      <div className="mt-4 flex flex-wrap gap-2" data-testid={`my-team-roster-${t.id}`}>
        {t.members.map((m, i) => <span key={i} className="text-sm rounded-full px-3 py-1" style={{ background: m.is_me ? "var(--sj-primary)" : "var(--sj-bg)", color: m.is_me ? "#fff" : "var(--sj-text-2)" }}>{m.name}{m.is_me && " (you)"}</span>)}
      </div>
    )}
  </div>
);

export default function MyTeams() {
  const [teams, setTeams] = useState(null);
  const [tab, setTab] = useState("mine");
  const load = useCallback(() => api.get("/portal/my-teams").then(({ data }) => setTeams(data)).catch(() => setTeams([])), []);
  useEffect(() => { load(); }, [load]);
  const leading = (teams || []).filter((t) => t.can_manage).length;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="my-teams-page">
      <div className="sj-hero p-8 md:p-10" data-testid="my-teams-hero">
        <div className="sj-eyebrow">Streams of Joy Erith London · My Teams</div>
        <h1 className="mt-2 font-head text-3xl md:text-4xl font-extrabold tracking-tight">My Teams</h1>
        <p className="mt-2 text-white/85">{leading ? `You lead ${leading} team${leading > 1 ? "s" : ""} — review applications, members and tasks below.` : "See the teams you serve on and apply to join others."}</p>
      </div>
      <MyTeamInvites onChange={load} />
      <div className="flex gap-2" data-testid="my-teams-tabs">
        {[["mine", "My teams", ShieldCheck], ["join", "Join a team", Users2]].map(([k, l, I]) => (
          <button key={k} data-testid={`my-teams-tab-${k}`} onClick={() => setTab(k)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors"
            style={tab === k ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", color: "var(--sj-text-2)", borderColor: "var(--sj-border)" }}><I className="h-4 w-4" /> {l}</button>
        ))}
      </div>
      {tab === "mine" && teams && teams.length === 0 && (
        <div className={`${CARD} p-12 text-center`} data-testid="my-teams-empty" style={{ color: "var(--sj-text-3)" }}>
          You're not on a team yet. <button onClick={() => setTab("join")} className="font-semibold underline" style={{ color: "var(--sj-primary)" }} data-testid="my-teams-empty-join">Browse teams to join</button>
        </div>
      )}
      {tab === "mine" && (teams || []).map((t) => <TeamCard key={t.id} t={t} reload={load} />)}
      {tab === "join" && <JoinTeamsPanel onApplied={load} />}
    </div>
  );
}
